// ⭐ Format date without timezone shift (YYYY-MM-DD)
export function formatDateLocal(d) {
  if (!d) return "";
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
}

// Normalize date to local midnight (no timezone jump)
function normalizeDate(d) {
  if (!(d instanceof Date)) {
    d = d.toDate ? d.toDate() : new Date(d);
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/* =========================================================================
   ASSIGNMENT PERIODS FINAL RULE:
   ✔ If assignments overlap → MERGE
   ✔ If no overlap → NEW period
   ✔ Different car → ALWAYS separate
   ✔ UI end date must show correct sheet range
   ✔ Logic end date must split income correctly
   ========================================================================= */
export function addAssignmentPeriods(assignmentsMap, monthStart, monthEnd) {
  const byCar = new Map();
  const final = [];

  // Group assignments by car
  Object.values(assignmentsMap).forEach((list) => {
    if (!list) return;
    list.forEach((a) => {
      if (!a.vehicleNumber) return;

      if (!byCar.has(a.vehicleNumber)) byCar.set(a.vehicleNumber, []);
      byCar.get(a.vehicleNumber).push(a);
    });
  });

  // Process each car separately
  byCar.forEach((assignments, carNumber) => {
    // Sort by real start date
    const sorted = [...assignments].sort((a, b) => a.startDate - b.startDate);

    let current = null;

    for (const a of sorted) {
      // REAL DATES
      const realStart = normalizeDate(a.startDate);
      const realEnd = a.endDate ? normalizeDate(a.endDate) : null;
            // ✅ SKIP assignments that do NOT intersect this month at all
      // (prevents ghost/blank sheet periods)
      if (realEnd && realEnd < monthStart) continue; // ended before month starts
      if (realStart > monthEnd) continue;            // starts after month ends


      // LOGIC START (clamped)
      const pStart = realStart < monthStart ? monthStart : realStart;

      // LOGIC END (infinite if null)
      const logicEnd = realEnd || new Date(9999, 0, 1);

      // Clamp to month end
      const pEnd = logicEnd > monthEnd ? monthEnd : logicEnd;
      pEnd.setHours(23, 59, 59, 999);
      // ✅ Safety: if clamping produced an invalid range, skip it
if (pEnd < pStart) continue;

      // UI END (for display only)
      const uiEnd = realEnd
        ? realEnd > monthEnd
          ? monthEnd
          : realEnd
        : monthEnd;

      const uiEndFinal = new Date(uiEnd);
      uiEndFinal.setHours(23, 59, 59, 999);

      if (!current) {
        current = {
          carNumber,
          carModel: a.vehicleType,
          company: a.company,
          periodStart: pStart,     // LOGIC START
          periodEnd: pEnd,         // LOGIC END
          uiPeriodEnd: uiEndFinal, // UI DATE
          driverIds: new Set([a.driverId]),
          driverNames: new Set([a.driverName]),
        };
        continue;
      }

      // Overlap = same car + date intersection
      const overlap = pStart <= current.periodEnd;

      if (overlap) {
        // Extend logic
        current.periodEnd = new Date(Math.max(current.periodEnd, pEnd));

        // Extend UI date
        current.uiPeriodEnd = new Date(Math.max(current.uiPeriodEnd, uiEndFinal));

        // Add driver to merged period
        current.driverIds.add(a.driverId);
        current.driverNames.add(a.driverName);

      } else {
        // No overlap → finalize
        final.push(current);

        current = {
          carNumber,
          carModel: a.vehicleType,
          company: a.company,
          periodStart: pStart,
          periodEnd: pEnd,
          uiPeriodEnd: uiEndFinal,
          driverIds: new Set([a.driverId]),
          driverNames: new Set([a.driverName]),
        };
      }
    }

    if (current) final.push(current);
  });

  return final.map((p) => {
  const driverIds = Array.from(p.driverIds);
  const driverNames = Array.from(p.driverNames);

  // Build driverPeriods map for UI
  const driverPeriods = {};
  driverIds.forEach((id, idx) => {
    driverPeriods[id] = [{
  driverId: id,
  driverName: driverNames[idx] || "",
  startDate: p.periodStart,
  endDate: p.uiPeriodEnd
}];

  });

  return {
    carNumber: p.carNumber,
    carModel: p.carModel,
    company: p.company,
    periodStart: p.periodStart,
    periodEnd: p.periodEnd,
    uiPeriodEnd: p.uiPeriodEnd,
    driverIds,
    driverNames,
    driverPeriods   // ⭐ NEW ⭐
  };
});

}

/* =========================================================================
   FIND PERIOD FOR A GIVEN DATE
   ========================================================================= */
export function findMergedPeriod(carNumber, date, assignmentPeriods) {
  const d = normalizeDate(date);
  return assignmentPeriods.find(
    (p) => p.carNumber === carNumber && d >= p.periodStart && d <= p.periodEnd
  );
}
